import { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { CreateQuoteDto } from '../../types/b2b';
import { User, Product } from '../../types';

interface FormData {
  customerId: string;
  validUntil: string;
  taxRate: number;
  discount: number;
  discountAmount: number;
  terms: string;
  notes: string;
  internalNotes: string;
  items: {
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    discount: number;
    unitDiscount: number;
    discountAmount: number;
  }[];
}

const fetchCustomers = async (): Promise<User[]> => {
  const response = await axiosInstance.get('/users?role=customer&limit=100');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchProducts = async (): Promise<Product[]> => {
  const response = await axiosInstance.get('/products?limit=100');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const QuoteCreate = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);

  const { data: customers, isLoading: customersLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: fetchCustomers,
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });

  const defaultValidUntil = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      customerId: '',
      validUntil: defaultValidUntil,
      taxRate: 19,
      discount: 0,
      discountAmount: 0,
      terms: '',
      notes: '',
      internalNotes: '',
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Watch entire form to get live updates on all field changes
  const formValues = watch();
  const watchItems = formValues.items || [];
  const watchTaxRate = formValues.taxRate;
  const watchDiscount = formValues.discount;
  const watchDiscountAmount = formValues.discountAmount;

  // Calculate totals directly (no useMemo to ensure live updates)
  // Subtotal = raw total before any discounts (qty × price)
  const subtotal = watchItems.reduce((sum: number, item: typeof watchItems[0]) => {
    const qty = Number(item?.quantity) || 0;
    const price = Number(item?.unitPrice) || 0;
    return sum + (price * qty);
  }, 0);

  // Item-level discounts (use discountAmount as source of truth since all 3 fields are linked)
  const itemDiscountsTotal = watchItems.reduce((sum: number, item: typeof watchItems[0]) => {
    const discAmount = Number(item?.discountAmount) || 0;
    return sum + discAmount;
  }, 0);

  const taxRateNum = Number(watchTaxRate) || 0;
  const discountRateNum = Number(watchDiscount) || 0;
  const fixedDiscountAmount = Number(watchDiscountAmount) || 0;

  // Overall discount = percentage discount + fixed amount
  const overallPercentageDiscount = subtotal * (discountRateNum / 100);
  const overallDiscountTotal = overallPercentageDiscount + fixedDiscountAmount;

  // Total discount = item discounts + overall discount
  const totalDiscountAmount = itemDiscountsTotal + overallDiscountTotal;

  // Tax is calculated on subtotal (before discounts)
  const taxAmount = subtotal * (taxRateNum / 100);

  // Final total = Subtotal + Tax - All Discounts
  const totalAmount = subtotal + taxAmount - totalDiscountAmount;

  const totals = { subtotal, tax: taxAmount, discount: totalDiscountAmount, total: totalAmount };

  const createMutation = useMutation({
    mutationFn: (data: CreateQuoteDto) => axiosInstance.post('/quotes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      showMessage('Quote created successfully', 'success');
      navigate('/app/quotes');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create quote', 'error');
    },
  });

  const onSubmit = (data: FormData) => {
    if (!selectedCustomer) {
      showMessage('Please select a customer', 'error');
      return;
    }

    if (data.items.length === 0) {
      showMessage('Please add at least one item', 'error');
      return;
    }

    const payload: CreateQuoteDto = {
      customerId: selectedCustomer.id,
      validUntil: data.validUntil,
      taxRate: Number(data.taxRate),
      discount: Number(data.discount),
      terms: data.terms || undefined,
      notes: data.notes || undefined,
      internalNotes: data.internalNotes || undefined,
      items: data.items.map((item) => ({
        productId: item.productId,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        discount: Number(item.discount) || 0,
      })),
    };

    createMutation.mutate(payload);
  };

  const handleAddProduct = (product: Product | null) => {
    if (product) {
      append({
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitPrice: product.price,
        discount: 0,
        unitDiscount: 0,
        discountAmount: 0,
      });
    }
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.surfaceElevated,
      borderRadius: theme.radius.sm,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: palette.primary },
      '&.Mui-focused fieldset': { borderColor: palette.primary },
    },
    '& .MuiInputLabel-root': { color: colors.textMuted },
    '& .MuiInputBase-input': { color: colors.textPrimary },
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/quotes')} sx={{ color: colors.textMuted, mb: 2, '&:hover': { color: palette.primary } }}>
          Back to Quotes
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <RequestQuoteIcon sx={{ color: palette.primary, fontSize: 24 }} />
          <Typography variant="overline" sx={{ color: palette.primary, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>
            B2B SUPPLIER SYSTEM
          </Typography>
        </Box>
        <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
          CREATE QUOTE
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          {/* Main Content */}
          <Box>
            {/* Customer & Details */}
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.1) }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 3, pb: 1, borderBottom: `2px solid ${colors.border}` }}>
                QUOTE DETAILS
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Autocomplete
                  options={customers || []}
                  loading={customersLoading}
                  value={selectedCustomer}
                  onChange={(_, value) => setSelectedCustomer(value)}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer *"
                      error={!selectedCustomer && errors.customerId !== undefined}
                      sx={inputStyles}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {customersLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <TextField
                  {...register('validUntil', { required: 'Valid until date is required' })}
                  label="Valid Until *"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.validUntil}
                  helperText={errors.validUntil?.message}
                  sx={inputStyles}
                />
              </Box>
            </Card>

            {/* Line Items */}
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.15) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.border}` }}>
                <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>
                  LINE ITEMS
                </Typography>
                <Autocomplete
                  options={products || []}
                  loading={productsLoading}
                  onChange={(_, value) => handleAddProduct(value)}
                  getOptionLabel={(option) => `${option.name} (${option.sku})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Product"
                      size="small"
                      sx={{ ...inputStyles, width: 300 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {productsLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  value={null}
                />
              </Box>

              {fields.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: colors.textMuted }}>No items added yet. Use the dropdown above to add products.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Product</TableCell>
                        <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Qty</TableCell>
                        <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Unit Price</TableCell>
                        <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Unit Disc</TableCell>
                        <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Disc %</TableCell>
                        <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Disc Amt</TableCell>
                        <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>Subtotal</TableCell>
                        <TableCell width={50}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => {
                        const item = watchItems[index];
                        const qty = Number(item?.quantity) || 0;
                        const price = Number(item?.unitPrice) || 0;
                        const discAmount = Number(item?.discountAmount) || 0;
                        const itemTotal = price * qty;
                        // Use discountAmount as the source of truth for subtotal calculation
                        const itemSubtotal = itemTotal - discAmount;

                        // Helper to update linked discount fields
                        const updateFromUnitDiscount = (newUnitDisc: number) => {
                          const newDiscAmount = newUnitDisc * qty;
                          const newDiscPercent = itemTotal > 0 ? (newDiscAmount / itemTotal) * 100 : 0;
                          return { unitDiscount: newUnitDisc, discount: Math.round(newDiscPercent * 100) / 100, discountAmount: Math.round(newDiscAmount * 100) / 100 };
                        };

                        const updateFromPercent = (newPercent: number) => {
                          const newDiscAmount = itemTotal * (newPercent / 100);
                          const newUnitDisc = qty > 0 ? newDiscAmount / qty : 0;
                          return { unitDiscount: Math.round(newUnitDisc * 100) / 100, discount: newPercent, discountAmount: Math.round(newDiscAmount * 100) / 100 };
                        };

                        const updateFromAmount = (newAmount: number) => {
                          const newDiscPercent = itemTotal > 0 ? (newAmount / itemTotal) * 100 : 0;
                          const newUnitDisc = qty > 0 ? newAmount / qty : 0;
                          return { unitDiscount: Math.round(newUnitDisc * 100) / 100, discount: Math.round(newDiscPercent * 100) / 100, discountAmount: newAmount };
                        };

                        return (
                          <TableRow key={field.id}>
                            <TableCell>
                              <Typography sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>{field.productName}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Controller
                                name={`items.${index}.quantity` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 60 }}
                                    inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                    onChange={(e) => f.onChange(Number(e.target.value) || 1)}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Controller
                                name={`items.${index}.unitPrice` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 90 }}
                                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                                    onChange={(e) => f.onChange(Number(e.target.value) || 0)}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Controller
                                name={`items.${index}.unitDiscount` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 80 }}
                                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                                    onChange={(e) => {
                                      const val = Number(e.target.value) || 0;
                                      const linked = updateFromUnitDiscount(val);
                                      f.onChange(linked.unitDiscount);
                                      setValue(`items.${index}.discount`, linked.discount);
                                      setValue(`items.${index}.discountAmount`, linked.discountAmount);
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Controller
                                name={`items.${index}.discount` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 55 }}
                                    inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                                    onChange={(e) => {
                                      const val = Number(e.target.value) || 0;
                                      const linked = updateFromPercent(val);
                                      f.onChange(linked.discount);
                                      setValue(`items.${index}.unitDiscount`, linked.unitDiscount);
                                      setValue(`items.${index}.discountAmount`, linked.discountAmount);
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Controller
                                name={`items.${index}.discountAmount` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 80 }}
                                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                                    onChange={(e) => {
                                      const val = Number(e.target.value) || 0;
                                      const linked = updateFromAmount(val);
                                      f.onChange(linked.discountAmount);
                                      setValue(`items.${index}.unitDiscount`, linked.unitDiscount);
                                      setValue(`items.${index}.discount`, linked.discount);
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.bold, color: palette.success }}>
                                {formatCurrency(itemSubtotal)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => remove(index)} sx={{ color: palette.error }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>

            {/* Notes */}
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, ...animations.slideUp(0.2) }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 3, pb: 1, borderBottom: `2px solid ${colors.border}` }}>
                ADDITIONAL INFORMATION
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField {...register('terms')} label="Terms & Conditions" multiline rows={3} fullWidth sx={inputStyles} />
                <TextField {...register('notes')} label="Customer Notes (visible to customer)" multiline rows={2} fullWidth sx={inputStyles} />
                <TextField {...register('internalNotes')} label="Internal Notes (not visible to customer)" multiline rows={2} fullWidth sx={inputStyles} />
              </Box>
            </Card>
          </Box>

          {/* Sidebar - Totals */}
          <Box>
            <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, position: 'sticky', top: 20, ...animations.slideUp(0.1) }}>
              <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 3, pb: 1, borderBottom: `2px solid ${colors.border}` }}>
                TOTALS
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <Controller
                  name="taxRate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Tax Rate (%)"
                      type="number"
                      fullWidth
                      size="small"
                      sx={inputStyles}
                      inputProps={{ min: 0, max: 100 }}
                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                    />
                  )}
                />
                <Controller
                  name="discount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Discount (%)"
                      type="number"
                      fullWidth
                      size="small"
                      sx={inputStyles}
                      inputProps={{ min: 0, max: 100 }}
                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                    />
                  )}
                />
                <Controller
                  name="discountAmount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Discount (Fixed Amount)"
                      type="number"
                      fullWidth
                      size="small"
                      sx={inputStyles}
                      inputProps={{ min: 0, step: 0.01 }}
                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.textMuted }}>Subtotal:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(totals.subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.textMuted }}>Tax ({watchTaxRate || 0}%):</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>{formatCurrency(totals.tax)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.textMuted }}>Discount ({watchDiscount || 0}%):</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: palette.error }}>-{formatCurrency(totals.discount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: `2px solid ${colors.border}` }}>
                  <Typography sx={{ fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>TOTAL:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.success, fontSize: typography.fontSize.lg }}>{formatCurrency(totals.total)}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={<SaveIcon />}
                  disabled={createMutation.isPending}
                  sx={{ backgroundColor: palette.primary, color: '#FFFFFF', fontWeight: typography.fontWeight.bold, py: 1.5, '&:hover': { backgroundColor: alpha(palette.primary, 0.8) } }}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Quote'}
                </Button>
                <Button variant="outlined" fullWidth onClick={() => navigate('/app/quotes')} sx={{ borderColor: colors.border, color: colors.textPrimary, '&:hover': { borderColor: colors.textMuted } }}>
                  Cancel
                </Button>
              </Box>
            </Card>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
